import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent,
  CardActions,
  Button, 
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab
} from '@mui/material';
import { 
  Search as SearchIcon,
  Download as DownloadIcon, 
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Assignment as AssignmentIcon,
  AssignmentLate as AssignmentLateIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Person as PersonIcon,
  CalendarMonth as CalendarMonthIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { useAuth } from '../auth/useAuth';
import { managedLocations, whereLocation } from '../auth/managedLocations';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit, 
  Timestamp, 
  collectionGroup,
  doc,
  getDoc,
  DocumentData,
  QueryDocumentSnapshot
} from 'firebase/firestore';
import { db } from '../../shared/utils/firebase';
import { format, parseISO, isValid } from 'date-fns';
import { LoadingSpinner } from '../../shared/components';
import { reportCategoryLabel } from './reportCategory';
import { canViewManualLactateReport, resolveReportClientName } from './reportVisibility';

// Define interfaces for our data
interface ClientReport {
  id: string;
  client_id: string;
  client_name: string;
  report_type: 'Initial' | 'Review';
  report_category?: 'lactate_threshold';
  url: string;
  download_url: string;
  generated_at: string; // ISO date string format "2025-03-10T01:27:47.239Z"
  report_status: string;
  created_at: Timestamp;
  lead_coach_name?: string;
  primary_location?: string;
}

interface ReportMetrics {
  totalReports: number;
  initialReports: number;
  reviewReports: number;
  reportsThisMonth: number;
  reportsLastMonth: number;
  reportsByLocation: Record<string, number>;
}

const Reports: React.FC = () => {
  const theme = useTheme();
  const { userRole, userMetadata, hasFeature } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reports, setReports] = useState<ClientReport[]>([]);
  const [filteredReports, setFilteredReports] = useState<ClientReport[]>([]);
  const [metrics, setMetrics] = useState<ReportMetrics>({
    totalReports: 0,
    initialReports: 0,
    reviewReports: 0,
    reportsThisMonth: 0,
    reportsLastMonth: 0,
    reportsByLocation: {}
  });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [reportTypeFilter, setReportTypeFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Available filter options
  const [availableClients, setAvailableClients] = useState<{id: string, name: string}[]>([]);
  const [availableLocations, setAvailableLocations] = useState<string[]>([]);
  
  // Function to transform name from "Lastname, Firstname" to "Firstname Lastname"
  const transformName = (name: string | null | undefined): string => {
    if (!name) return 'Unknown';
    
    // Check if the name is in "Lastname, Firstname" format
    if (name.includes(',')) {
      const [lastName, firstName] = name.split(',').map(part => part.trim());
      return `${firstName} ${lastName}`;
    }
    
    return name;
  };
  
  // Format date
  const formatDate = (dateValue: any): string => {
    if (!dateValue) return 'N/A';
    
    try {
      // Handle ISO date strings (e.g., "2025-03-10T01:27:47.239Z")
      if (typeof dateValue === 'string') {
        const parsedDate = parseISO(dateValue);
        if (isValid(parsedDate)) {
          return format(parsedDate, 'dd MMM yyyy, h:mm a');
        }
      }
      
      // Check if timestamp is a Firestore Timestamp
      if (dateValue && typeof dateValue.toDate === 'function') {
        return format(dateValue.toDate(), 'dd MMM yyyy, h:mm a');
      }
      
      // Check if timestamp is already a Date object
      if (dateValue instanceof Date) {
        return format(dateValue, 'dd MMM yyyy, h:mm a');
      }
      
      // Check if timestamp is a number (seconds or milliseconds)
      if (typeof dateValue === 'number') {
        // If seconds (Firestore uses seconds)
        if (dateValue < 10000000000) {
          return format(new Date(dateValue * 1000), 'dd MMM yyyy, h:mm a');
        }
        // If milliseconds
        return format(new Date(dateValue), 'dd MMM yyyy, h:mm a');
      }
      
      // Check if timestamp has seconds and nanoseconds (Firestore Timestamp structure)
      if (dateValue && typeof dateValue.seconds === 'number') {
        const date = new Date(dateValue.seconds * 1000);
        return format(date, 'dd MMM yyyy, h:mm a');
      }
      
      console.log('Unrecognized date format:', dateValue);
      return 'Invalid Date Format';
    } catch (error) {
      console.error('Error formatting date:', error, 'Date value:', JSON.stringify(dateValue));
      return 'Invalid Date';
    }
  };
  
  // Calculate metrics from reports
  const calculateMetrics = useCallback((reportsList: ClientReport[]): ReportMetrics => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
    const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;
    
    const initialReports = reportsList.filter(r => r.report_type === 'Initial').length;
    const reviewReports = reportsList.filter(r => r.report_type === 'Review').length;
    
    const reportsThisMonth = reportsList.filter(r => {
      const date = parseISO(r.generated_at);
      return date.getMonth() === thisMonth && date.getFullYear() === thisYear;
    }).length;
    
    const reportsLastMonth = reportsList.filter(r => {
      const date = parseISO(r.generated_at);
      return date.getMonth() === lastMonth && date.getFullYear() === lastMonthYear;
    }).length;
    
    // Group by location
    const reportsByLocation: Record<string, number> = {};
    reportsList.forEach(report => {
      if (report.primary_location) {
        reportsByLocation[report.primary_location] = (reportsByLocation[report.primary_location] || 0) + 1;
      }
    });
    
    return {
      totalReports: reportsList.length,
      initialReports,
      reviewReports,
      reportsThisMonth,
      reportsLastMonth,
      reportsByLocation
    };
  }, []);
  
  // Filter reports based on search and filters
  const filterReports = useCallback(() => {
    const filtered = reports.filter(report => {
      // Search term filter
      const matchesSearch = searchTerm === '' || 
        (report.client_name && transformName(report.client_name).toLowerCase().includes(searchTerm.toLowerCase())) ||
        (report.client_id && report.client_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (report.report_type && report.report_type.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Client filter
      const matchesClientFilter = clientFilter === '' || report.client_id === clientFilter;
      
      // Report type filter
      const matchesReportTypeFilter = reportTypeFilter === '' || report.report_type === reportTypeFilter;
      
      // Location filter
      const matchesLocationFilter = locationFilter === '' || 
        (report.primary_location && report.primary_location === locationFilter);
      
      return matchesSearch && matchesClientFilter && matchesReportTypeFilter && matchesLocationFilter;
    });
    
    // Sort by date (newest first)
    filtered.sort((a, b) => {
      const dateA = parseISO(a.generated_at);
      const dateB = parseISO(b.generated_at);
      return dateB.getTime() - dateA.getTime();
    });
    
    setFilteredReports(filtered);
  }, [reports, searchTerm, clientFilter, reportTypeFilter, locationFilter]);
  
  // Fetch reports from Firestore
  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Create a query based on user role
        let reportsQuery;
        
        if (userRole === 'ADMIN') {
          // Admins can see all reports
          reportsQuery = collectionGroup(db, 'reports');
        } else if (userRole === 'MANAGER' && managedLocations(userMetadata).length > 0) {
          // For managers, we need to first get clients in their locations
          const clientsQuery = query(
            collection(db, 'bigquery_cache/client_data/comprehensive_details'),
            whereLocation('primary_location', managedLocations(userMetadata))
          );
          
          const clientsSnapshot = await getDocs(clientsQuery);
          const clientIds = clientsSnapshot.docs.map(doc => doc.id);
          
          // Then get reports for those clients
          if (clientIds.length > 0) {
            // Since we can't directly query across subcollections with a where clause on the parent,
            // we'll need to fetch all reports and filter in memory
            reportsQuery = collectionGroup(db, 'reports');
          } else {
            // No clients found
            setReports([]);
            setFilteredReports([]);
            setMetrics({
              totalReports: 0,
              initialReports: 0,
              reviewReports: 0,
              reportsThisMonth: 0,
              reportsLastMonth: 0,
              reportsByLocation: {}
            });
            setLoading(false);
            return;
          }
        } else if (userRole === 'USER' && userMetadata?.coach_id) {
          // For coaches, we need to first get their clients
          const leadCoachQuery = query(
            collection(db, 'bigquery_cache/client_data/comprehensive_details'),
            where('lead_coach_id', '==', userMetadata.coach_id)
          );
          
          const collaboratorQuery = query(
            collection(db, 'bigquery_cache/client_data/comprehensive_details'),
            where('collaborating_coach_ids', 'array-contains', userMetadata.coach_id)
          );
          
          const [leadCoachSnapshot, collaboratorSnapshot] = await Promise.all([
            getDocs(leadCoachQuery),
            getDocs(collaboratorQuery)
          ]);
          
          const clientIds = [
            ...leadCoachSnapshot.docs.map(doc => doc.id),
            ...collaboratorSnapshot.docs.map(doc => doc.id)
          ];
          
          // Then get reports for those clients
          if (clientIds.length > 0) {
            // Since we can't directly query across subcollections with a where clause on the parent,
            // we'll need to fetch all reports and filter in memory
            reportsQuery = collectionGroup(db, 'reports');
          } else {
            // No clients found
            setReports([]);
            setFilteredReports([]);
            setMetrics({
              totalReports: 0,
              initialReports: 0,
              reviewReports: 0,
              reportsThisMonth: 0,
              reportsLastMonth: 0,
              reportsByLocation: {}
            });
            setLoading(false);
            return;
          }
        } else {
          // Default empty query
          setReports([]);
          setFilteredReports([]);
          setMetrics({
            totalReports: 0,
            initialReports: 0,
            reviewReports: 0,
            reportsThisMonth: 0,
            reportsLastMonth: 0,
            reportsByLocation: {}
          });
          setLoading(false);
          return;
        }
        
        // Execute query
        const snapshot = await getDocs(reportsQuery);
        
        // Get client data for each report
        const clientsQuery = collection(db, 'bigquery_cache/client_data/comprehensive_details');
        const clientsSnapshot = await getDocs(clientsQuery);
        
        const clientsMap = new Map();
        clientsSnapshot.docs.forEach(doc => {
          clientsMap.set(doc.id, doc.data());
        });
        
        // Process reports
        let fetchedReports: ClientReport[] = [];
        // Legacy manual-client lactate reports (generated before client_name was
        // written to the pointer) need their name recovered from the source test
        // doc; collect them here and resolve after the main pass.
        const fallbackTargets: { index: number; clientId: string; sourceTestId: string }[] = [];
        
        snapshot.docs.forEach(doc => {
          // Extract client_id from the path
          const pathSegments = doc.ref.path.split('/');
          const clientIndex = pathSegments.indexOf('clients');
          
          if (clientIndex !== -1 && clientIndex + 1 < pathSegments.length) {
            const clientId = pathSegments[clientIndex + 1];
            const clientData = clientsMap.get(clientId);
            const reportData = doc.data();
            const isManualLactate =
              !clientData && reportData.report_category === 'lactate_threshold';

            // Filter based on user role. Manual/new clients have no directory
            // record, so they are not location/coach scoped — they are visible to
            // admins, managers, and users with the lactate report-generation
            // permission, and hidden from everyone else.
            if (isManualLactate) {
              if (!canViewManualLactateReport(userRole, hasFeature('lactate_testing'))) {
                return;
              }
            } else {
              if (userRole === 'MANAGER' &&
                  managedLocations(userMetadata).length > 0 &&
                  !managedLocations(userMetadata).includes(clientData?.primary_location)) {
                return;
              }

              if (userRole === 'USER' && userMetadata?.coach_id &&
                  clientData?.lead_coach_id !== userMetadata.coach_id &&
                  !(clientData?.collaborating_coach_ids &&
                    clientData.collaborating_coach_ids.includes(userMetadata.coach_id))) {
                return;
              }
            }

            // Prefer the name stored on the pointer (self-describing); fall back to
            // the directory cache. Legacy manual-client pointers have neither — the
            // typed name is recovered from the source test doc after this pass.
            const pointerName =
              typeof reportData.client_name === 'string' ? reportData.client_name : undefined;
            const resolvedName = resolveReportClientName(pointerName, clientData?.client_name);

            fetchedReports.push({
              id: doc.id,
              client_id: clientId,
              client_name: resolvedName,
              report_type: reportData.report_type || 'Initial',
              report_category: reportData.report_category,
              url: reportData.url || '',
              download_url: reportData.download_url || reportData.url?.replace('https://drive.google.com/file/d/', 'https://drive.google.com/uc?export=download&id=')?.replace('/view?usp=sharing', '') || '',
              generated_at: reportData.generated_at || reportData.created_at.toISOString(),
              report_status: reportData.report_status || 'Completed',
              created_at: reportData.created_at || Timestamp.now(),
              lead_coach_name: clientData?.lead_coach_name,
              primary_location: clientData?.primary_location
            });

            if (
              resolvedName === 'Unknown Client' &&
              reportData.report_category === 'lactate_threshold' &&
              typeof reportData.source_test_id === 'string'
            ) {
              fallbackTargets.push({
                index: fetchedReports.length - 1,
                clientId,
                sourceTestId: reportData.source_test_id,
              });
            }
          }
        });
        
        // On-read fallback for legacy manual-client lactate reports: recover the
        // typed name from clients/{clientId}/lactate_tests/{sourceTestId} (the
        // pointer's source_test_id). New reports already carry client_name.
        if (fallbackTargets.length > 0) {
          await Promise.all(
            fallbackTargets.map(async (t) => {
              try {
                const testSnap = await getDoc(
                  doc(db, 'clients', t.clientId, 'lactate_tests', t.sourceTestId)
                );
                const testName = testSnap.exists()
                  ? (testSnap.data() as DocumentData).client_name
                  : undefined;
                if (typeof testName === 'string' && testName.trim()) {
                  fetchedReports[t.index].client_name = testName;
                }
              } catch {
                // Leave as 'Unknown Client' if the test doc cannot be read.
              }
            })
          );
        }

        // Extract unique clients and locations for filters
        const clients = new Map<string, {id: string, name: string}>();
        const locations = new Set<string>();
        
        fetchedReports.forEach(report => {
          clients.set(report.client_id, {
            id: report.client_id,
            name: report.client_name
          });
          
          if (report.primary_location) {
            locations.add(report.primary_location);
          }
        });
        
        setAvailableClients(Array.from(clients.values()));
        setAvailableLocations(Array.from(locations));
        
        // Sort by date (newest first)
        fetchedReports.sort((a, b) => {
          const dateA = parseISO(a.generated_at);
          const dateB = parseISO(b.generated_at);
          return dateB.getTime() - dateA.getTime();
        });
        
        setReports(fetchedReports);
        setFilteredReports(fetchedReports);
        setMetrics(calculateMetrics(fetchedReports));
        
      } catch (error) {
        console.error('Error fetching reports:', error);
        setError('Failed to load reports. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReports();
  }, [userRole, userMetadata, hasFeature, calculateMetrics]);
  
  // Apply filters when they change
  useEffect(() => {
    filterReports();
  }, [filterReports, searchTerm, clientFilter, reportTypeFilter, locationFilter]);
  
  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when search changes
  };
  
  // Handle client filter change
  const handleClientFilterChange = (event: SelectChangeEvent) => {
    setClientFilter(event.target.value);
    setPage(0);
  };
  
  // Handle report type filter change
  const handleReportTypeFilterChange = (event: SelectChangeEvent) => {
    setReportTypeFilter(event.target.value);
    setPage(0);
  };
  
  // Handle location filter change
  const handleLocationFilterChange = (event: SelectChangeEvent) => {
    setLocationFilter(event.target.value);
    setPage(0);
  };
  
  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setClientFilter('');
    setReportTypeFilter('');
    setLocationFilter('');
    setPage(0);
  };
  
  // Group reports by client
  const reportsByClient = filteredReports.reduce((acc, report) => {
    if (!acc[report.client_id]) {
      acc[report.client_id] = {
        clientName: transformName(report.client_name),
        clientId: report.client_id,
        reports: []
      };
    }
    
    acc[report.client_id].reports.push(report);
    return acc;
  }, {} as Record<string, { clientName: string; clientId: string; reports: ClientReport[] }>);
  
  const [tabValue, setTabValue] = React.useState('allReports');

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Reports Repository
      </Typography>
      
      {/* Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Reports
              </Typography>
              <Typography variant="h4">
                {metrics.totalReports}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {metrics.initialReports} Initial / {metrics.reviewReports} Review
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Reports This Month
              </Typography>
              <Typography variant="h4">
                {metrics.reportsThisMonth}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {metrics.reportsThisMonth > metrics.reportsLastMonth 
                  ? `↑ ${metrics.reportsThisMonth - metrics.reportsLastMonth} from last month`
                  : `↓ ${metrics.reportsLastMonth - metrics.reportsThisMonth} from last month`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Clients with Reports
              </Typography>
              <Typography variant="h4">
                {Object.keys(reportsByClient).length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Out of {availableClients.length} total clients
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Average Reports per Client
              </Typography>
              <Typography variant="h4">
                {Object.keys(reportsByClient).length > 0 
                  ? (metrics.totalReports / Object.keys(reportsByClient).length).toFixed(1) 
                  : '0'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {metrics.totalReports} reports across {Object.keys(reportsByClient).length} clients
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by client name, ID, or report type..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel id="client-filter-label">Client</InputLabel>
              <Select
                labelId="client-filter-label"
                value={clientFilter}
                onChange={handleClientFilterChange}
                label="Client"
              >
                <MenuItem value="">All Clients</MenuItem>
                {availableClients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {transformName(client.name)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel id="report-type-filter-label">Report Type</InputLabel>
              <Select
                labelId="report-type-filter-label"
                value={reportTypeFilter}
                onChange={handleReportTypeFilterChange}
                label="Report Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="Initial">Initial</MenuItem>
                <MenuItem value="Review">Review</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel id="location-filter-label">Location</InputLabel>
              <Select
                labelId="location-filter-label"
                value={locationFilter}
                onChange={handleLocationFilterChange}
                label="Location"
              >
                <MenuItem value="">All Locations</MenuItem>
                {availableLocations.map((location) => (
                  <MenuItem key={location} value={location}>{location}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Button 
              fullWidth 
              variant="outlined" 
              onClick={handleClearFilters}
              startIcon={<FilterListIcon />}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      <Box sx={{ width: '100%' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="reports view tabs"
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          <Tab label="All Reports" value="allReports" icon={<AssignmentIcon />} iconPosition="start" />
          <Tab label="By Client" value="byClient" icon={<PersonIcon />} iconPosition="start" />
          <Tab label="Timeline" value="timeline" icon={<TimelineIcon />} iconPosition="start" />
        </Tabs>
      </Box>
      
      {tabValue === 'allReports' && (
        <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {loading ? (
            <LoadingSpinner />
          ) : filteredReports.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography variant="h6" color="textSecondary">
                No reports found. Try adjusting your filters.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Client</TableCell>
                      <TableCell>Report Type</TableCell>
                      <TableCell>Generated</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Coach</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredReports
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((report) => (
                        <TableRow key={`${report.client_id}-${report.id}`}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                              <Box>
                                <Typography variant="body1">
                                  {transformName(report.client_name)}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {report.client_id}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                              <Chip
                                icon={report.report_type === 'Initial' ? <AssignmentIcon /> : <AssignmentLateIcon />}
                                label={report.report_type}
                                color={report.report_type === 'Initial' ? 'primary' : 'secondary'}
                                size="small"
                              />
                              {reportCategoryLabel(report.report_category) && (
                                <Chip
                                  label={reportCategoryLabel(report.report_category)}
                                  size="small"
                                  variant="outlined"
                                  sx={{ borderColor: '#AB292C', color: '#AB292C' }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <CalendarMonthIcon sx={{ mr: 1, fontSize: 'small', color: 'text.secondary' }} />
                              <Typography variant="body2">
                                {formatDate(report.generated_at)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{report.primary_location || 'N/A'}</TableCell>
                          <TableCell>{transformName(report.lead_coach_name)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Report">
                              <IconButton 
                                color="primary" 
                                component="a" 
                                href={report.url} 
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Report">
                              <IconButton 
                                color="secondary" 
                                component="a" 
                                href={report.download_url} 
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredReports.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </Paper>
      )}
      
      {tabValue === 'byClient' && (
        <Box>
          {loading ? (
            <LoadingSpinner />
          ) : filteredReports.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography variant="h6" color="textSecondary">
                No reports found. Try adjusting your filters.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">
                  Reports by Client
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Showing {Object.keys(reportsByClient).length} clients with {filteredReports.length} reports
                </Typography>
              </Box>
              
              <Grid container spacing={3}>
                {Object.values(reportsByClient).map((clientData) => (
                  <Grid item xs={12} md={6} lg={4} key={clientData.clientId}>
                    <Card sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      transition: 'all 0.3s',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-4px)'
                      }
                    }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box>
                            <Typography variant="h6" gutterBottom>
                              {clientData.clientName}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              Client ID: {clientData.clientId}
                            </Typography>
                          </Box>
                          <Chip 
                            label={`${clientData.reports.length} Reports`} 
                            color="primary" 
                            size="small"
                          />
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Box sx={{ 
                          maxHeight: '300px', 
                          overflowY: 'auto',
                          pr: 1,
                          '&::-webkit-scrollbar': {
                            width: '8px',
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: 'rgba(0,0,0,0.1)',
                            borderRadius: '4px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(0,0,0,0.2)',
                            borderRadius: '4px',
                          }
                        }}>
                          {clientData.reports.map((report, index) => (
                            <Box key={report.id} sx={{ 
                              mb: 2,
                              pb: 2,
                              borderBottom: index < clientData.reports.length - 1 ? '1px dashed rgba(0, 0, 0, 0.1)' : 'none'
                            }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {report.report_type === 'Initial' ? (
                                    <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                                  ) : (
                                    <AssignmentLateIcon sx={{ mr: 1, color: 'secondary.main' }} />
                                  )}
                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {report.report_type} Report
                                  </Typography>
                                </Box>
                              </Box>
                              
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                <CalendarMonthIcon sx={{ mr: 0.5, fontSize: '0.9rem' }} />
                                {formatDate(report.generated_at)}
                              </Typography>
                              
                              <CardActions sx={{ pt: 1, px: 0 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  component="a"
                                  href={report.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<DownloadIcon />}
                                  component="a"
                                  href={report.download_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Download
                                </Button>
                              </CardActions>
                            </Box>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Box>
      )}
      
      {tabValue === 'timeline' && (
        <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {loading ? (
            <LoadingSpinner />
          ) : filteredReports.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography variant="h6" color="textSecondary">
                No reports found. Try adjusting your filters.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="h5" gutterBottom>
                Reports Timeline
              </Typography>
              
              <Box sx={{ position: 'relative', mt: 4, mb: 2, ml: 2 }}>
                {/* Vertical timeline line */}
                <Box sx={{ 
                  position: 'absolute', 
                  left: 16, 
                  top: 0, 
                  bottom: 0, 
                  width: 2, 
                  bgcolor: 'divider',
                  zIndex: 0
                }} />
                
                {/* Timeline items */}
                {filteredReports
                  .sort((a, b) => {
                    // Sort by timestamp, handling potential undefined or invalid timestamps
                    const dateA = parseISO(a.generated_at);
                    const dateB = parseISO(b.generated_at);
                    return dateB.getTime() - dateA.getTime();
                  })
                  .map((report, index) => {
                    // Get date from report using our robust formatDate function
                    let reportDate;
                    try {
                      // Try to get a JavaScript Date object for grouping
                      reportDate = parseISO(report.generated_at);
                    } catch (e) {
                      console.error('Error parsing date for grouping:', e);
                      reportDate = new Date();
                    }
                    
                    const monthYear = format(reportDate, 'MMMM yyyy');
                    
                    // Check if this is the first report of a new month
                    let isNewMonth = index === 0;
                    
                    if (index > 0) {
                      let prevReportDate;
                      try {
                        // Get previous report date using same logic
                        prevReportDate = parseISO(filteredReports[index - 1].generated_at);
                        
                        const prevMonthYear = format(prevReportDate, 'MMMM yyyy');
                        isNewMonth = monthYear !== prevMonthYear;
                      } catch (e) {
                        console.error('Error comparing dates for month grouping:', e);
                        isNewMonth = false;
                      }
                    }
                    
                    return (
                      <React.Fragment key={`${report.client_id}-${report.id}`}>
                        {isNewMonth && (
                          <Box sx={{ 
                            position: 'relative', 
                            mb: 3, 
                            ml: 4, 
                            mt: index > 0 ? 4 : 0 
                          }}>
                            <Typography variant="h6" color="primary">
                              {monthYear}
                            </Typography>
                          </Box>
                        )}
                        
                        <Box sx={{ 
                          position: 'relative', 
                          mb: 3, 
                          ml: 4, 
                          display: 'flex',
                          alignItems: 'flex-start'
                        }}>
                          {/* Timeline dot */}
                          <Box sx={{ 
                            position: 'absolute', 
                            left: -20, 
                            top: 0, 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            bgcolor: report.report_type === 'Initial' ? 'primary.main' : 'secondary.main',
                            border: '2px solid white',
                            zIndex: 1
                          }} />
                          
                          <Card sx={{ 
                            width: '100%',
                            transition: 'all 0.3s',
                            '&:hover': {
                              boxShadow: 6,
                              transform: 'translateY(-2px)'
                            }
                          }}>
                            <CardContent>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                  <Typography variant="body2" color="textSecondary">
                                    {formatDate(report.generated_at)}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                    <Chip
                                      icon={report.report_type === 'Initial' ? <AssignmentIcon /> : <AssignmentLateIcon />}
                                      label={report.report_type}
                                      color={report.report_type === 'Initial' ? 'primary' : 'secondary'}
                                      size="small"
                                      sx={{ mr: 1 }}
                                    />
                                    <Typography variant="body2" color="textSecondary">
                                      {report.primary_location || 'No location'}
                                    </Typography>
                                  </Box>
                                </Box>
                                
                                <Box sx={{ display: 'flex' }}>
                                  <Tooltip title="View Report">
                                    <IconButton 
                                      size="small"
                                      color="primary" 
                                      component="a" 
                                      href={report.url} 
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <VisibilityIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Download Report">
                                    <IconButton 
                                      size="small"
                                      color="secondary" 
                                      component="a" 
                                      href={report.download_url} 
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <DownloadIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                              
                              <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <PersonIcon sx={{ mr: 1, color: 'primary.main', fontSize: '1.2rem' }} />
                                  <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                      {transformName(report.client_name)}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                      Coach: {transformName(report.lead_coach_name)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Box>
                      </React.Fragment>
                    );
                  })}
              </Box>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default Reports;
